import React from 'react';
import { Dialog, DialogContent, DialogTitle, IconButton } from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { Document, Page, PDFViewer, Text, View, StyleSheet } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: {
    padding: 60,
    fontSize: 12,
    fontFamily: 'Times-Roman',
    lineHeight: 1.5,
  },
  title: {
    fontSize: 14,
    fontFamily: 'Times-Bold',
    textAlign: 'center',
    marginBottom: 30,
    textDecoration: 'underline',
  },
  paragraph: {
    textAlign: 'justify',
    marginBottom: 25,
    lineHeight: 1.8,
  },
  sectionTitle: {
    fontSize: 12,
    fontFamily: 'Times-Bold',
    textAlign: 'center',
    marginTop: 20,
    marginBottom: 10,
    textDecoration: 'underline',
  },
  itemRow: {
    marginBottom: 3,
    paddingLeft: 20,
  },
  closingText: {
    textAlign: 'center',
    marginTop: 30,
    marginBottom: 10,
  },
  dateLocation: {
    textAlign: 'center',
    marginBottom: 50,
  },
  signatureSection: {
    alignItems: 'center',
    marginTop: 40,
  },
  signatureName: {
    fontFamily: 'Times-Bold',
    textAlign: 'center',
    marginTop: 60,
  },
  signatureLabel: {
    textAlign: 'center',
  },
});

interface DeclaracaoData {
  nome: string;
  cpf: string;
  rg: string;
  nacionalidade: string;
  estado_civil: string;
  rua: string;
  numero: string;
  bairro: string;
  cidade: string;
  estado: string;
  bens_moveis: string;
  bens_imoveis: string;
  outros_bens: string;
}

interface DeclaracaoPatrimonioPDFProps {
  open: boolean;
  onClose: () => void;
  data: DeclaracaoData;
}

const formatDate = () => {
  const months = [
    'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
    'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
  ];
  const date = new Date();
  return `${date.getDate()} de ${months[date.getMonth()]} de ${date.getFullYear()}`;
};

const parseItems = (items: string): string[] => {
  if (!items || items.trim() === '') return [];
  return items.split(',').map(item => item.trim()).filter(item => item !== '');
};

const DeclaracaoDocument = ({ data }: { data: DeclaracaoData }) => {
  const bensMoveis = parseItems(data.bens_moveis);
  const bensImoveis = parseItems(data.bens_imoveis);
  const outrosBens = parseItems(data.outros_bens);

  return (
    // @ts-ignore
    <Document>
      {/* @ts-ignore */}
      <Page size="A4" style={styles.page}>
        <Text style={styles.title}>DECLARAÇÃO DE BENS E VALORES</Text>

        <Text style={styles.paragraph}>
          {'        '}Eu, <Text style={{ fontFamily: 'Times-Bold' }}>{data.nome || '_______________'}</Text>, nacionalidade {data.nacionalidade || '_______________'}, {data.estado_civil || '_______________'}, portador da cédula de identidade n.º {data.rg || '_______________'} SSP/MS, inscrito no cadastro de pessoas físicas sob n.º {data.cpf || '_______________'}, residente e domiciliado na Rua: {data.rua || '_______________'}, n.º {data.numero || '___'} – Bairro: {data.bairro || '_______________'}, neste Município de Ribas do Rio Pardo/MS, <Text style={{ fontFamily: 'Times-Bold' }}>DECLARO</Text> ser possuidor dos seguintes Bens e Valores:
        </Text>

        <Text style={styles.sectionTitle}>BENS IMÓVEIS</Text>
        {bensImoveis.length > 0 ? (
          bensImoveis.map((bem, index) => (
            <View key={index} style={styles.itemRow}>
              <Text>{bem}</Text>
            </View>
          ))
        ) : (
          <View style={styles.itemRow}>
            <Text>Nenhum bem imóvel declarado.</Text>
          </View>
        )}

        <Text style={styles.sectionTitle}>BENS MÓVEIS</Text>
        {bensMoveis.length > 0 ? (
          bensMoveis.map((bem, index) => (
            <View key={index} style={styles.itemRow}>
              <Text>{bem}</Text>
            </View>
          ))
        ) : (
          <View style={styles.itemRow}>
            <Text>Nenhum bem móvel declarado.</Text>
          </View>
        )}

        <Text style={styles.sectionTitle}>OUTROS BENS E VALORES</Text>
        {outrosBens.length > 0 ? (
          outrosBens.map((bem, index) => (
            <View key={index} style={styles.itemRow}>
              <Text>{bem}</Text>
            </View>
          ))
        ) : (
          <View style={styles.itemRow}>
            <Text>Nenhum outro bem ou valor declarado.</Text>
          </View>
        )}

        <Text style={styles.closingText}>
          Por ser verdade, assino a presente declaração.
        </Text>

        <Text style={styles.dateLocation}>
          Ribas do Rio Pardo, MS, {formatDate()}.
        </Text>

        <View style={styles.signatureSection}>
          <Text style={styles.signatureName}>{data.nome || 'NOME'}</Text>
          <Text style={styles.signatureLabel}>Declarante</Text>
        </View>
      </Page>
    </Document>
  );
};

export default function DeclaracaoPatrimonioPDF({ open, onClose, data }: DeclaracaoPatrimonioPDFProps) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Declaração de Patrimônio - {data.nome}</span>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent>
        <PDFViewer style={{ width: '100%', height: '80vh' }}>
          <DeclaracaoDocument data={data} />
        </PDFViewer>
      </DialogContent>
    </Dialog>
  );
}
